import { getImageMetadata } from '@carboncopyco/editor-utils';
import type { ImageFileMetadata } from '@carboncopyco/types';
import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';
import type { ImageDetails } from '../ImageInput';

export const useImageUrlDetails = (details?: ImageDetails) => {
  const [status, setStatus] = useState<'idle' | 'error' | 'loading'>();
  const [imageDetails, setImageDetails] = useState<ImageDetails>();

  useEffect(() => {
    if (!details) {
      setStatus('idle');
      setImageDetails(null);
      return;
    }

    if (details.url === imageDetails?.url) {
      return;
    }

    if (details.metadata && isMetadataComplete(details.metadata)) {
      setImageDetails(details);
      return;
    }

    if (!details.url || details.url?.startsWith('data:image/')) {
      setStatus('error');
      setImageDetails(null);
      return;
    }

    let abortImageLoading = false;

    const loadImage = async (url: string) => {
      try {
        setStatus('loading');
        const metadata = await getImageMetadata(url);
        if (abortImageLoading) {
          return;
        }

        setImageDetails({
          url,
          metadata,
        });
        setStatus('idle');
      } catch (e) {
        console.error(e);
        setImageDetails(null);
        setStatus('error');
      }
    };

    loadImage(details.url);

    return () => {
      abortImageLoading = true;
    };
  }, [details, imageDetails]);

  return [imageDetails, status] as const;
};

const isMetadataComplete = ({
  name,
  format,
  width,
  height,
}: ImageFileMetadata) => ![name, format, width, height].some(isNil);
